<template>
  <div>
    <div class="dashboard-table-actions">
      <filter-bar tab-name="liens" />
    </div>

    <CoreTable
      ref="vuetable"
      :loading="loading"
      http-method="post"
      :api-url="apiUrl"
      :http-fetch="customFetch"
      :http-options="httpOptions"
      :initial-page="currentPageNumber"
      :fields="fields"
      pagination-path="result"
      data-path="result.data"
      :sort-order="sortOrder"
      multi-sort
      :append-params="moreParams"
      :per-page="perPage"
      show-sort-icons
      track-by="uuid"
      @vuetable:pagination-data="onPaginationData"
      @vuetable:load-error="onLoadError"
      @table:perPageChange="handlePageChange"
    >
      <template #renter="{ rowData }">
        <span
          class="underline flex w-max cursor-pointer"
          @click="() => onClickTenant(rowData.uuid)"
        >
          {{ rowData.renter }}
        </span>
      </template>

      <template #cellphone="{ rowData }">
        <a
          v-if="rowData.cellphone"
          :href="'tel:'+rowData.cellphone"
          target="_blank"
          class="underline text-primary"
          v-text="rowData.cellphone"
        />
        <span
          v-else
          class="font-medium"
          v-text="'None'"
        />
      </template>

      <template #formatStatusSlot="{ rowData }">
        <span
          :data-tooltip="statuses[rowData.status]?.description"
          class="tooltip has-tooltip-top has-tooltip-multiline td-child"
        >
          <span
            class="py-1 px-2 rounded-lg"
            :style="{
              'color': statuses[rowData.status]['foreground'],
              'background': statuses[rowData.status]['background'],
            }"
          >{{ statuses[rowData.status].name }}</span>
        </span>
      </template>

      <template
        slot="overdue"
        slot-scope="props"
      >
        <template v-if="props.rowData.overdue !== null">
          <div class="next-notification">
            <span
              v-if="props.rowData.overdue == 0"
              class="tag is-rounded is-warning has-text-weight-bold"
              style="margin-right: .4em;"
            >
              Today
            </span>
            <span
              v-else-if="props.rowData.overdue > 0"
              class="tag is-rounded is-info has-text-weight-bold"
              :style="{backgroundColor: role === roles.SUPER_ADMIN ? '#00d1b2 !important' : null, marginRight: '.4em'}"
            >
              Not due yet
              <span class="ml-1">{{ pluralize(Math.abs(props.rowData.overdue), 'Day') }}</span>
            </span>
            <span
              v-else
              class="tag is-rounded is-danger has-text-weight-bold"
              :style="{ marginRight: '.4em'}"
            >
              Late
              <span class="ml-1">{{ pluralize(Math.abs(props.rowData.overdue), 'Day') }}</span>
            </span>
          </div>
        </template>
        <template v-else>
          None
        </template>
      </template>

      <template
        slot="delay"
        slot-scope="props"
      >
        {{ pluralize(Math.abs(parseInt(props.rowData.delay)), 'Day') }}
      </template>

      <template
        slot="table-actions"
      >
        <!-- Table Config -->
        <div class="dashboard-table-config">
          <div
            v-if="showTableConfig"
            class="overlay fixed h-full w-full z-10 right-0 top-0"
            @click="showTableConfig = false"
          />
          <div class="flex flex-col relative">
            <div
              class="
                h-10
                bg-white
                rounded
                flex
                items-center
                border
                justify-center
                dark:bg-muted-700
                dark:border-muted-600
                hover:border-muted-400
                dark:hover:bg-muted-700
                dark:hover:border-muted-700
                border-muted-300
                text-muted-900
                dark:hover:text-white
                whitespace-nowrap
                text-sm
                navbar-link
                has-dropdown
              "
              @click="showTableConfig = !showTableConfig"
            >
              <i class="fas fa-cog" />
              <span class="lg:hidden ml-2">Table Settings</span>
            </div>

            <div
              class="bg-white border py-4 border-muted-300 dark:bg-muted-700 dark:border-muted-600 rounded absolute top-11 z-20 right-0 shadow px-4"
              :class="{ 'hidden': !showTableConfig }"
            >
              <label
                v-for="(header, index) in tableHeaders"
                :key="index"
                class="checkbox flex navbar-item whitespace-nowrap"
              >
                <input
                  v-model="header.enabled"
                  type="checkbox"
                  @change="changeHeaders"
                >
                <div class=""> {{ header.title }}</div>
              </label>
            </div>
          </div>
        </div>

        <div
          class="button secondary"
          data-tooltip="Export results to Excel"
          @click="exportExcel()"
        >
          <i class="fas fa-file-excel" />
          <span class="lg:hidden ml-2">Export Results</span>
        </div>
      </template>
    </CoreTable>
  </div>
</template>

<script>
import Vue from 'vue';
import VueEvents from 'vue-events';
import Axios from 'axios';
import DashboardService from '../../../services/dashboard.service';
import CustomActions from './CustomActions';
import FilterBarForLien from './NewFilterBar.vue';

import CoreTable from '@/components/core/Table';

import { Roles } from '../../../constants';
import { generateDashboardFilters } from '../utils';

const dashboardService = new DashboardService();

Vue.use(VueEvents);
Vue.component('CustomActions', CustomActions);
Vue.component('FilterBar', FilterBarForLien);

export default {
  components: {
    CoreTable
  },
  data() {
    return {
      apiUrl: `${this.$store.state.API_URL}view/all_liens/${this.$store.state.company.uuid}`,
      httpOptions: {},
      companyName: '',
      selectedOrganizationUuid: null,
      perPage: this.$store.state.pageNumbers.dashboard.liens.perPage,
      sortOrder: [
        { field: 'status', sortField: 'status', direction: 'asc' }
      ],
      statuses: {},
      moreParams: {},
      role: this.$store.state.user.role,
      roles: Roles,
      isFilterChanged: false,
      filters: {},
      searchValue: '',
      locationName: '',
      next: '',
      previous: '',
      overdue: '',
      status: '',
      currentPageNumber: this.$store.state.pageNumbers.dashboard.liens.pageNumber || 20,
      loading: false,
      showTableConfig: false
    };
  },
  computed: {
    isNotAdmin() {
      return Roles.CS_ADMIN === this.$store.state.user.role
        || (Roles.SUPER_ADMIN !== this.$store.state.user.role && Roles.CS_ADMIN !== this.$store.state.user.role);
    },
    fields() {
      return this.$store.getters['TableState/getDashboardTableHeaders'](this.isNotAdmin);
    },
    tableHeaders() {
      return this.$store.getters['TableState/getDashboardTableOptions'](this.isNotAdmin);
    }
  },
  watch: {
    async isFilterChanged() {
      this.httpOptions = { ...this.httpOptions, filters: generateDashboardFilters(this.filters) };

      await this.$nextTick();
      this.$refs.vuetable.refresh();
    }
  },
  mounted() {
    let companyName = '';
    if (Roles.MANAGER === this.$store.state.user.role) {
      companyName = this.$store.state.company.name;
    }
    this.companyName = companyName;

    // Init table headers presists
    this.$store.commit('TableState/setInitHeaders');
  },
  beforeMount() {
    this.getStatusColors();
    const organization = this.$store.state.organization || {};
    const organizationUuid = organization && Object.keys(organization).length ? organization.uuid : '';
    this.selectedOrganizationUuid = organizationUuid;
    let apiUrl = `${this.$store.state.API_URL}view/all_liens/${organizationUuid}`;
    if (Roles.CS_ADMIN === this.$store.state.user.role) {
      apiUrl = `${this.$store.state.API_URL}view/all_liens/${this.$store.state?.organization?.uuid}`;
    } else if (Roles.SUPER_ADMIN !== this.$store.state.user.role && Roles.CS_ADMIN !== this.$store.state.user.role) {
      apiUrl = `${this.$store.state.API_URL}view/all_liens/${this.$store.state?.locationsList[0]?.organization_uuid}`;
    }
    this.apiUrl = apiUrl;

    // Apply the saved Filters
    this.$store.commit('TableState/setInitFilters');
    this.filters = this.$store.getters['TableState/getDashboardTableFilters'];
    this.httpOptions = { ...this.httpOptions, filters: generateDashboardFilters(this.filters) };
  },
  methods: {
    pluralize(count, noun, suffix = 's') {
      return `${count} ${noun}${count !== 1 ? suffix : ''}`;
    },
    customFetch(apiUrl, httpOptions, options = {}) {
      return Axios.post(apiUrl, httpOptions, { headers: { Authorization: 'Bearer '.concat(this.$store.state.accessToken) }, ...options });
    },
    async exportExcel() {
      const { data: blob } = await this.customFetch(
        `${this.$store.state.API_URL}view/export/`,
        this.httpOptions,
        { responseType: 'blob' }
      );
      const preparedUrl = window.URL.createObjectURL(blob);
      window.open(preparedUrl, '_blank').focus();
    },
    getStatusColors() {
      (async () => {
        try {
          const res = await dashboardService.getDashboardStatusColors();
          if (res.data.status === 'success') {
            this.statuses = res.data.result;
          }
        } catch (err) {
          this.$root.$emit('showToast', {
            message: err,
            options: { cssClass: 'is-danger', duration: 10000 }
          });
        }
      })();
    },
    onPaginationData(paginationData) {
      const { current_page: currentPage, per_page: perPage } = paginationData;
      this.currentPageNumber = currentPage;
      this.$store.commit('setPageNumber', {
        key: 'dashboard',
        tabName: 'liens',
        value: { perPage, pageNumber: currentPage }
      });
    },
    onClickTenant(uuid) {
      const routeData = this.$router.resolve({ name: `/tenant/view/${uuid}` });
      window.open(routeData.location.name, '_blank');
    },
    onLoadError() {
      this.$root.$emit('showToast', { message: 'Impossible to perform this action. Be sure to be connected and to have access to this action', options: { cssClass: 'is-danger', duration: 10000 } });
    },
    changeHeaders() {
      this.$store.commit('TableState/setDashboardTableHeaders', this.tableHeaders);
    },
    handlePageChange(e) {
      this.moreParams.per_page = e;
      this.$store.commit('setPageNumber', {
        key: 'dashboard',
        tabName: 'liens',
        value: { perPage: e }
      });
      Vue.nextTick(() => this.$refs.vuetable.refresh());
    },
    getNextActionName(data) {
      return [data.next_action, data.next_action_name].filter((it) => it).join(': ');
    }
  },
  events: {
    'filters-set': function (filters) {
      this.filters = filters;
      this.isFilterChanged = !this.isFilterChanged;
    },
    'filters-reset': function (defaultFilter) {
      this.filters = defaultFilter;
      this.isFilterChanged = !this.isFilterChanged;
    },
    refresh() {
      Vue.nextTick(() => this.$refs.vuetable.refresh());
    }
  }
};
</script>
<style
  scoped
  src="../../../../public/assets/css/bulma-extension/bulma-tooltip.min.css"
></style>
<style scoped lang="scss">
    hr {
        display: block;
        margin-top: 0;
        margin-bottom: 0.5rem;
        margin-left: auto;
        margin-right: auto;
        border-style: inset;
        border-width: 1px;
    }
    .underline{
        text-decoration: underline;
    }
    .dashboard-table-actions {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .vuetable {

        .next-notification {
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .notification-type {
                margin-top: 5px;
            }
        }

        .actions {
            display: flex;
            justify-content: flex-end;

            .select {
                select {
                    min-width: 75px;
                }
            }

            .pagination {
                margin-left: 25px;
            }
        }
    }

    .dashboard-table-config {
        display: flex;
        justify-content: flex-end;
        align-items: center;
    }

    :deep {
      td {
        white-space: pre-line;
        min-width: 150px;
      }
    }
</style>
